'use client';

import type { i18n as I18nInstance } from 'i18next';

import { I18N_COOKIE_NAME } from './i18n.settings';

/**
 * Switches the active i18n locale AND persists the choice in the `lang`
 * cookie so the SSR-rendered shell uses the same locale on the next page
 * load (or after a force-quit / cold-start of the Capacitor app).
 *
 * Without this, the in-memory i18n instance switches locales but the
 * server-side i18n init falls back to the Accept-Language header on the
 * very next request — undoing the user's choice.
 *
 * Cookie scope: path=/ so every route sees it; max-age=1y so it survives
 * across sessions; SameSite=Lax so it sticks through navigations.
 */
export async function persistLanguage(
  i18n: I18nInstance,
  locale: string,
): Promise<void> {
  if (typeof document !== 'undefined') {
    const oneYear = 60 * 60 * 24 * 365;
    document.cookie = `${I18N_COOKIE_NAME}=${encodeURIComponent(locale)}; path=/; max-age=${oneYear}; SameSite=Lax`;
  }
  await i18n.changeLanguage(locale);
}
